import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
    AppScreen, Section, SectionHeader, SearchInput, FilterBar, ListCard, EmptyState, Button, Sheet, ActionList, ActionRow,
} from 'components/ds';
import styles from './PlaceSearch.module.css';
import { isNativeApp, openNativePlaceExplore } from 'common/webview';
import { useUserLocation } from 'hooks/useUserLocation';
import { LocationLoading } from 'components/common/LocationLoading';
import { isInKorea } from 'common/utils/regionDetector';
import { PlaceMapView, MapPlace } from 'components/map/PlaceMapView';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import config from 'config/config';
import { fetchPlacePhotos } from 'common/utils/placePhotos';
import { distanceKm, haversineMeters } from 'common/utils/geo';

const defaultCenter = { lat: 37.5665, lng: 126.9780 };

/** 한 번에 그리는 개수. 스크롤 끝에 닿으면 이만큼씩 늘립니다. */
const PAGE = 12;

/**
 * 장소 찾기.
 *
 * 예전에는 지도가 화면 전체였고 목록은 지도 안에 얹혀 있었습니다. 그러면
 * "고를 후보를 훑는" 일이 어려워서, 목록을 앞에 두고 지도를 버튼으로 뺐습니다.
 * 참고소스(일상의틈 공간탭)의 매장 목록과 같은 골격입니다 —
 *   검색창 → 카테고리·정렬 손잡이 → 카드 목록 → 결과 없으면 추천으로 대체.
 *
 * 데이터를 다루는 부분(기준 좌표 우선순위·주변 조회·해외 네이티브 위임·
 * 사진 보강·결과 반환)은 예전 화면 그대로입니다. 화면만 새로 짰습니다.
 */
export const PlaceSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentLocation, setCurrentLocation] = useState(defaultCenter);
    const [nearbyPlaces, setNearbyPlaces] = useState<MapPlace[]>([]);
    // 위치 측위 완료 여부 — 성공/실패/타임아웃 무관. 실패 시 defaultCenter 로 fallback 하여 디폴트 추천 노출.
    const [locationResolved, setLocationResolved] = useState(false);
    const { location: cachedLocation, fetchLocation: fetchUserLocation } = useUserLocation();
    // 캐시 위치도 없고 아직 측위 결과도 안 왔을 때만 로딩바 표시
    const isLocationLoading = !cachedLocation && !locationResolved;

    // 화면 상태 — 검색어·카테고리·정렬·더 보기·지도 모드
    const [keyword, setKeyword] = useState('');
    const [category, setCategory] = useState('all');
    const [sortBy, setSortBy] = useState<'near' | 'rating'>('near');
    const [showSortSheet, setShowSortSheet] = useState(false);
    const [shown, setShown] = useState(PAGE);
    const [mapOpen, setMapOpen] = useState(false);

    // 기존 장소 정보가 있으면 center로 사용
    const placeInfo = location.state?.placeInfo;
    // SubPlaceModal 진입 — anchor 우선순위: 이전 sub_place > 호텔 > 여행지 > placeInfo
    const subFlow = !!location.state?.fromSubPlaceModal;
    const prevSubCenter = subFlow ? (location.state as any)?.prevSubPlaceCenter as { lat: number; lng: number } | null | undefined : null;
    const hotelCenter = subFlow ? (location.state as any)?.hotelCenter as { lat: number; lng: number } | null | undefined : null;
    const travelDestinationCenter = subFlow
        ? (location.state as any)?.travelDestinationCenter as { lat: number; lng: number } | null | undefined
        : null;
    const stateIsTravel = subFlow ? !!(location.state as any)?.isTravelMeeting : false;
    // 여행 모임은 절대 한국 디폴트(서울 시청)로 fallback 하면 안 됨 — 해당 국가 anchor 우선.
    const initialCenter = (prevSubCenter && prevSubCenter.lat && prevSubCenter.lng)
        ? { lat: prevSubCenter.lat, lng: prevSubCenter.lng }
        : (stateIsTravel && hotelCenter && hotelCenter.lat && hotelCenter.lng)
            ? { lat: hotelCenter.lat, lng: hotelCenter.lng }
            : (stateIsTravel && travelDestinationCenter && travelDestinationCenter.lat && travelDestinationCenter.lng)
                ? { lat: travelDestinationCenter.lat, lng: travelDestinationCenter.lng }
                : (placeInfo?.lat && placeInfo?.lng
                    ? { lat: parseFloat(placeInfo.lat), lng: parseFloat(placeInfo.lng) }
                    : null);

    /* 거리를 어디에서 재는지. "1.2km" 만 보여 주면 어디서부터인지 알 수 없습니다. */
    const anchorCenter = (prevSubCenter && prevSubCenter.lat && prevSubCenter.lng)
        ? { lat: prevSubCenter.lat, lng: prevSubCenter.lng }
        : (stateIsTravel && hotelCenter && hotelCenter.lat && hotelCenter.lng)
            ? { lat: hotelCenter.lat, lng: hotelCenter.lng }
            : currentLocation;
    const anchorLabel = prevSubCenter
        ? t('place.fromPrevPlace')
        : (stateIsTravel && hotelCenter ? t('place.fromHotel') : t('placeExplore.anchorMe'));

    // 해외 + 네이티브 → 추천목록 조회 후 네이티브 탐색 화면(openNativePlaceExplore)으로 위임.
    // 즉시 위임하지 않고 nearbyPlaces 조회를 기다렸다 한 번만 연다 (아래 fetchNearby 안에서 처리).
    const nativeExploreOpenedRef = useRef(false);

    // 현재 위치 가져오기 — useUserLocation 캐시 우선, 실패/타임아웃 시 defaultCenter 유지
    useEffect(() => {
        if (initialCenter) {
            // 여행 모임 + 사용자가 여행지 근처(~300km 이내, 같은 국가/지역)에 있으면 실제 GPS 우선.
            // 여행 도중 현지에서 검색 시 destination center 보다 사용자 위치가 더 유용.
            if (stateIsTravel && travelDestinationCenter && cachedLocation) {
                const dx = cachedLocation.lat - travelDestinationCenter.lat;
                const dy = cachedLocation.lng - travelDestinationCenter.lng;
                // haversine 대신 평면 근사 — ~111km/도 가정. 3° ≈ 333km.
                const roughDeg = Math.sqrt(dx * dx + dy * dy);
                if (roughDeg < 3) {
                    setCurrentLocation({ lat: cachedLocation.lat, lng: cachedLocation.lng });
                    setLocationResolved(true);
                    return;
                }
            }
            setCurrentLocation(initialCenter);
            setLocationResolved(true);
            return;
        }
        // 캐시된 위치가 있으면 즉시 사용
        if (cachedLocation) {
            setCurrentLocation({ lat: cachedLocation.lat, lng: cachedLocation.lng });
            setLocationResolved(true);
            // 캐시 만료면 백그라운드 갱신
            fetchUserLocation();
            return;
        }
        // 캐시가 없으면 새로 요청 — 성공이든 실패(타임아웃)든 끝나면 resolved 표시
        fetchUserLocation()
            .then(loc => {
                if (loc) setCurrentLocation({ lat: loc.lat, lng: loc.lng });
                // 실패/null 이면 defaultCenter 그대로 사용 — 디폴트 지역 주변 추천 노출
            })
            .finally(() => setLocationResolved(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 주변 추천 장소 조회 (Google Places — 사진/별점 포함)
    // 측위 완료 전에는 조회 보류. 완료 후엔 defaultCenter 여도 해당 지역(서울 시청) 기준으로 조회.
    useEffect(() => {
        if (!locationResolved) return;
        if (!currentLocation) return;

        const fetchNearby = async () => {
            // 빠진 가게 줄이기 위해 카테고리 다양화 + limit/radius 확대
            const types = stateIsTravel
                ? ['attraction', 'restaurant', 'cafe', 'shopping', 'entertainment']
                : ['restaurant', 'cafe', 'bar', 'entertainment', 'sports'];
            const results: MapPlace[] = [];

            for (const type of types) {
                try {
                    const res = await instance.get(getApiUrl('nearbyPlaces'), {
                        params: { lat: currentLocation.lat, lng: currentLocation.lng, type, radius: 5000, limit: 25 }
                    });
                    const data = res?.data?.data || [];
                    for (const p of data) {
                        if (results.find(r => r.name === p.name)) continue;
                        results.push({
                            id: p.placeId || `nearby-${results.length}`,
                            name: p.name,
                            address: p.address || p.vicinity || '',
                            lat: p.lat,
                            lng: p.lng,
                            imageUrl: undefined, // 사진은 /photos API로 별도 요청
                            rating: p.rating || undefined,
                            reviewCount: p.userRatingsTotal || undefined,
                            categoryIcon: type === 'restaurant' ? 'restaurant' : type === 'cafe' ? 'cafe' : type === 'bar' ? 'bar' : type === 'attraction' ? 'attraction' : type === 'shopping' ? 'shopping' : type === 'sports' ? 'sports' : 'activity',
                            categoryLabel: type === 'restaurant' ? t('place.category.restaurant') : type === 'cafe' ? t('place.category.cafe') : type === 'bar' ? t('place.category.bar') : type === 'attraction' ? t('place.category.attraction') : type === 'shopping' ? t('place.category.shopping') : type === 'sports' ? t('place.category.sports') : t('place.category.activity'),
                        });
                    }
                } catch { /* skip */ }
            }

            // SubPlaceModal 진입 시 — 이전 장소 거리 → 호텔 거리 → 평점
            if (subFlow && (prevSubCenter || hotelCenter)) {
                const haversineKm = distanceKm;
                results.sort((a, b) => {
                    if (prevSubCenter) {
                        const aD = haversineKm(prevSubCenter, { lat: a.lat, lng: a.lng });
                        const bD = haversineKm(prevSubCenter, { lat: b.lat, lng: b.lng });
                        if (aD !== bD) return aD - bD;
                    }
                    if (hotelCenter) {
                        const aD = haversineKm(hotelCenter, { lat: a.lat, lng: a.lng });
                        const bD = haversineKm(hotelCenter, { lat: b.lat, lng: b.lng });
                        if (aD !== bD) return aD - bD;
                    }
                    return (b.rating || 0) - (a.rating || 0);
                });
                if (prevSubCenter) {
                    results.forEach(p => {
                        const km = haversineKm(prevSubCenter, { lat: p.lat, lng: p.lng });
                        (p as any).distance = km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
                    });
                }
            }

            // 해외 + 네이티브 → 추천목록 + 검색 + 리뷰가 있는 네이티브 탐색 화면으로 위임 (한 번만).
            // 국내/웹 브라우저는 아래 웹 목록·지도를 그대로 사용.
            const exploreCenter = initialCenter || currentLocation;
            if (isNativeApp() && exploreCenter && !isInKorea(exploreCenter.lat, exploreCenter.lng) && !nativeExploreOpenedRef.current) {
                nativeExploreOpenedRef.current = true;
                // rev4-F · mode 결정 로직 · location.state 기반
                const stateHotelOnly = !!(location.state as any)?.hotelOnlyMode;
                const stIsTravel = !!(location.state as any)?.isTravelMeeting;
                const nativeMode: 'hotel' | 'attraction' | 'general' = stateHotelOnly
                    ? 'hotel'
                    : stIsTravel
                        ? 'attraction'
                        : 'general';
                const result = await openNativePlaceExplore({
                    places: results.filter(p => p.lat && p.lng).map(p => ({
                        id: p.id, name: p.name, address: p.address || '', lat: p.lat, lng: p.lng,
                        imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                    })),
                    center: exploreCenter,
                    confirmButtonText: subFlow ? t('subPlace.mapSheetConfirm') : t('place.selectThisLocation'),
                    title: subFlow ? t('subPlace.title') : t('place.title'),
                    apiUrl: config.API_URL,
                    mode: nativeMode,
                });
                if (result) {
                    navigateBack({
                        name: result.name,
                        address: result.address,
                        lat: result.lat,
                        lng: result.lng,
                        source: 'google',  // 해외 네이티브 구글 지도에서 선택
                    });
                } else {
                    navigate(-1);
                }
                return;
            }

            setNearbyPlaces(results);

            // 사진 배치 요청 (처음 보이는 항목들)
            const placeIds = results.slice(0, PAGE).map(p => p.id).filter(Boolean);
            if (placeIds.length > 0) {
                try {
                    const photos = await fetchPlacePhotos(placeIds);
                    if (Object.keys(photos).length > 0) {
                        setNearbyPlaces(prev => prev.map(p =>
                            photos[p.id] ? {
                                ...p,
                                imageUrl: photos[p.id].photoUrl || p.imageUrl,
                                rating: photos[p.id].rating ?? p.rating
                            } : p
                        ));
                    }
                } catch { /* 사진 로딩 실패 시 아이콘 유지 */ }
            }
        };

        fetchNearby();
    }, [currentLocation, locationResolved]); // eslint-disable-line react-hooks/exhaustive-deps

    // 사진 배치 요청 — 지도(스와이프)와 목록(더 보기) 양쪽에서 씁니다
    const photoLoadedRef = useRef<Set<string>>(new Set());
    const loadPhotosFor = useCallback(async (ids: string[]) => {
        const newIds = ids.filter(id => id && !photoLoadedRef.current.has(id));
        if (newIds.length === 0) return;
        newIds.forEach(id => photoLoadedRef.current.add(id));
        try {
            const photos = await fetchPlacePhotos(newIds);
            if (Object.keys(photos).length > 0) {
                setNearbyPlaces(prev => prev.map(p =>
                    photos[p.id] ? {
                        ...p,
                        imageUrl: photos[p.id].photoUrl || p.imageUrl,
                        rating: photos[p.id].rating ?? p.rating
                    } : p
                ));
            }
        } catch {
            newIds.forEach(id => photoLoadedRef.current.delete(id));
        }
    }, []);
    const handleVisiblePlacesChange = useCallback((visibleIds: string[]) => {
        loadPhotosFor(visibleIds);
    }, [loadPhotosFor]);

    /* X 로 닫을 때 — 서브 장소를 고르던 중이면 그 창으로 돌아갑니다.
       고르던 입력(장소명·일자·시간)을 그대로 되살립니다. */
    const closeToCaller = () => {
        const st: any = location.state || {};
        if (subFlow && st.returnTo) {
            navigate(st.returnTo, {
                replace: true,
                state: {
                    fromSubPlaceModal: true,
                    subPlaceForm: st.subPlaceForm,
                    editingSubPlaceId: st.editingSubPlaceId,
                    isCustomVisitTime: st.isCustomVisitTime,
                    meetingId: st.meetingId,
                },
            });
            return;
        }
        navigate(-1);
    };

    // 결과 반환 (기존 state 유지)
    const navigateBack = (place: {
        name: string;
        address: string;
        lat: number;
        lng: number;
        imageUrl?: string;
        rating?: number;
        reviewCount?: number;
        categoryLabel?: string;
        source?: 'kakao' | 'google';
    }) => {
        const newState = {
            ...location.state,
            selectedPlace: place.name,
            placeInfo: {
                name: place.name,
                address: place.address,
                lat: place.lat,
                lng: place.lng,
                imageUrl: place.imageUrl,
                rating: place.rating,
                reviewCount: place.reviewCount,
                categoryLabel: place.categoryLabel,
                source: place.source,
            },
        };
        const returnTo = location.state?.returnTo || '/meeting/create/step3';
        navigate(returnTo, { state: newState, replace: true });
    };

    const handlePlaceConfirm = (place: MapPlace) => {
        navigateBack({
            name: place.name,
            address: place.address,
            lat: place.lat,
            lng: place.lng,
            imageUrl: place.imageUrl,
            rating: place.rating,
            reviewCount: place.reviewCount,
            categoryLabel: place.categoryLabel,
            source: 'kakao',  // 한국 카카오 지도에서 선택
        });
    };

    // 기존 장소를 places 목록에 포함
    const initialPlaces: MapPlace[] = [];
    if (placeInfo?.name && placeInfo?.lat && placeInfo?.lng) {
        initialPlaces.push({
            id: 'current-place',
            name: placeInfo.name,
            address: placeInfo.address || '',
            lat: parseFloat(placeInfo.lat),
            lng: parseFloat(placeInfo.lng),
            categoryIcon: 'pin',
            categoryLabel: t('place.directSelect'),
        });
    }

    const allPlaces = [
        ...initialPlaces,
        ...nearbyPlaces.filter(p => !initialPlaces.find(ip => ip.name === p.name)),
    ];

    /* 화면에 보일 목록 — 검색어·카테고리로 좁히고 고른 순서로 정렬합니다.
       서버를 다시 부르지 않고 이미 받아 온 목록 안에서 거릅니다 (호출 비용 없음). */
    const filtered = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const list = allPlaces.filter(p => {
            if (category !== 'all' && p.categoryIcon !== category) return false;
            if (!q) return true;
            return `${p.name} ${p.address || ''} ${p.categoryLabel || ''}`.toLowerCase().includes(q);
        });
        return list.slice().sort((a, b) => {
            if (a.id === 'current-place') return -1;
            if (b.id === 'current-place') return 1;
            if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
            const da = haversineMeters(anchorCenter.lat, anchorCenter.lng, a.lat, a.lng);
            const db = haversineMeters(anchorCenter.lat, anchorCenter.lng, b.lat, b.lng);
            return da - db;
        });
    }, [allPlaces, keyword, category, sortBy, anchorCenter.lat, anchorCenter.lng]);

    // 검색어·카테고리가 바뀌면 처음부터 다시 봅니다
    useEffect(() => { setShown(PAGE); }, [keyword, category, sortBy]);

    const visible = filtered.slice(0, shown);

    // 화면에 새로 들어온 항목의 사진을 채웁니다
    useEffect(() => {
        loadPhotosFor(visible.map(p => p.id));
    }, [shown, keyword, category]); // eslint-disable-line react-hooks/exhaustive-deps

    const distOf = (p: MapPlace) => {
        if (!p.lat || !p.lng) return '';
        const m = haversineMeters(anchorCenter.lat, anchorCenter.lng, p.lat, p.lng);
        return m < 1000 ? `${Math.round(m)}m` : `${(m / 1000).toFixed(1)}km`;
    };
    const subOf = (p: MapPlace) => [
        p.categoryLabel || '',
        p.rating ? `${p.rating}` : '',
        p.address || '',
    ].filter(Boolean).join(' · ');

    // 카테고리 손잡이 — 지금 목록에 실제로 있는 것만 보여 줍니다
    const categoryOptions = useMemo(() => {
        const seen = new Map<string, string>();
        allPlaces.forEach(p => {
            if (p.categoryIcon && p.categoryLabel && !seen.has(p.categoryIcon)) {
                seen.set(p.categoryIcon, p.categoryLabel);
            }
        });
        return [
            { value: 'all', label: t('place.category.all') },
            ...Array.from(seen.entries()).map(([value, label]) => ({ value, label })),
        ];
    }, [allPlaces, t]);

    // 해외+네이티브로 explore 화면에 위임할 상황이면 웹 화면을 아예 렌더하지 않는다.
    // (네이티브 모달이 뜨기 전 웹 화면이 잠깐 보이는 플래시 방지)
    const willDelegateNative = isNativeApp() && (
        initialCenter
            ? !isInKorea(initialCenter.lat, initialCenter.lng)
            : (locationResolved ? !isInKorea(currentLocation.lat, currentLocation.lng) : true)
    );

    const distanceReferenceCenter = (prevSubCenter && prevSubCenter.lat && prevSubCenter.lng)
        ? { lat: prevSubCenter.lat, lng: prevSubCenter.lng }
        : (stateIsTravel && hotelCenter && hotelCenter.lat && hotelCenter.lng
            ? { lat: hotelCenter.lat, lng: hotelCenter.lng }
            : null);

    /* 지도 — 예전에는 이 화면 자체가 지도였습니다. 후보를 훑기 어려워서
       목록을 앞에 두고, 지도는 눌러서 여는 전체 화면으로 옮겼습니다. */
    if (mapOpen && !willDelegateNative) {
        return (
            <div className={styles.mapScreen}>
                <PlaceMapView
                    places={allPlaces}
                    center={currentLocation}
                    selectedPlaceId={initialPlaces.length > 0 ? 'current-place' : undefined}
                    onPlaceSelect={() => {}}
                    onPlaceConfirm={handlePlaceConfirm}
                    onVisiblePlacesChange={handleVisiblePlacesChange}
                    onCenterChange={(c) => setCurrentLocation(c)}
                    showSearch={true}
                    showBackButton={true}
                    onClose={() => setMapOpen(false)}
                    showCategories={false}
                    confirmButtonText={t('place.selectThisLocation')}
                    distanceReferenceCenter={distanceReferenceCenter}
                    distanceReferenceLabel={prevSubCenter ? t('place.fromPrevPlace') : t('place.fromHotel')}
                />
            </div>
        );
    }

    return (
        /* X 는 왔던 자리로 — 기본 닫기는 "마지막 탭으로 replace" 라, 숙박을
           고르다 누르면 모임 만들기가 통째로 사라지고 메인탭이 떴습니다.
           여기는 무언가를 고르러 들어온 하위 화면이라 고르기만 그만둡니다. */
        <AppScreen title={t('place.search')} bar="sub" onClose={closeToCaller}>
            <LocationLoading visible={isLocationLoading || willDelegateNative} />

            {!willDelegateNative && (
                <>
                    <Section spacing="tight">
                        <SearchInput
                            value={keyword}
                            onChange={setKeyword}
                            placeholder={t('place.searchPlaceholder')}
                            clearLabel={t('common.clear')}
                        />
                        <FilterBar
                            options={categoryOptions}
                            value={category}
                            onChange={setCategory}
                            ariaLabel={t('placeExplore.categoryLabel')}
                            sortLabel={sortBy === 'near' ? t('placeExplore.sortNear') : t('placeExplore.sortRating')}
                            onSort={() => setShowSortSheet(true)}
                        />
                        <p className={styles.anchorNote}>
                            {t('placeExplore.anchorLabel')} · {anchorLabel}
                        </p>
                    </Section>

                    <Section divided last>
                        <SectionHeader
                            title={t('place.nearbyPopular')}
                            moreLabel={t('place.mapSearch')}
                            onMore={() => setMapOpen(true)}
                        />

                        {visible.length > 0 ? (
                            <>
                                <div className={styles.list}>
                                    {visible.map(p => (
                                        <ListCard
                                            key={p.id}
                                            title={p.name}
                                            sub={subOf(p)}
                                            imageUrl={p.imageUrl}
                                            aside={distOf(p)}
                                            onClick={() => handlePlaceConfirm(p)}
                                        />
                                    ))}
                                </div>
                                {filtered.length > visible.length && (
                                    <Button
                                        block
                                        variant="secondary"
                                        onClick={() => setShown(n => n + PAGE)}
                                    >
                                        {t('common.more')}
                                    </Button>
                                )}
                            </>
                        ) : (
                            /* 결과가 없을 때 빈 화면으로 두지 않습니다.
                               참고소스도 "이런 매장은 어때요?" 추천으로 갈아끼웁니다. */
                            <EmptyState
                                title={locationResolved ? t('place.noPlaces') : t('place.searching')}
                                action={
                                    keyword ? (
                                        <Button onClick={() => setKeyword('')}>{t('common.clear')}</Button>
                                    ) : (
                                        <Button onClick={() => setMapOpen(true)}>{t('place.mapSearch')}</Button>
                                    )
                                }
                            />
                        )}
                    </Section>

                    <Sheet
                        open={showSortSheet}
                        title={t('placeExplore.sortLabel')}
                        closeLabel={t('common.close')}
                        onClose={() => setShowSortSheet(false)}
                    >
                        <ActionList>
                            {([['near', t('placeExplore.sortNear')], ['rating', t('placeExplore.sortRating')]] as const).map(([v, label]) => (
                                <ActionRow
                                    key={v}
                                    title={label}
                                    desc={sortBy === v ? t('common.selected') : undefined}
                                    onClick={() => { setSortBy(v as 'near' | 'rating'); setShowSortSheet(false); }}
                                />
                            ))}
                        </ActionList>
                    </Sheet>
                </>
            )}
        </AppScreen>
    );
};
